import "./SeletorMultiplo.css";

/**
 * Escolher **vários** de uma lista curta, tocando um a um.
 *
 * <p>O `SeletorDeEmpresa` escolhe **um** entre poucos; este escolhe **quantos
 * se quiser**, e a diferença não é de contagem: ali a escolha troca o que a
 * tela mostra, aqui ela decide **o que vai acontecer** — quais tambores recebem
 * adesivo, e portanto quais emissões ficam gravadas.
 *
 * <p>Ele nasceu na tela de etiquetas (#34), onde a alternativa era escrever a
 * grade de números dentro do setor. Componente visual nasce em
 * {@code packages/ui} porque "escolher vários" vai aparecer de novo — volumes
 * hoje, recipientes de um mutirão amanhã —, e duas grades com formas
 * ligeiramente diferentes para o mesmo gesto é como uma interface passa a
 * induzir erro.
 *
 * <h2>Por que botão, e não caixa de seleção</h2>
 *
 * <p>Vinte caixas de 18 × 13 px empilhadas não se acertam com o polegar de
 * luva, e o piso de 44 px da casa não se alcança esticando a bolinha — ele se
 * alcança fazendo do **alvo inteiro** o controle. Cada opção é um
 * {@code <button aria-pressed>}: a plataforma já sabe anunciar "pressionado",
 * o teclado já chega nele, e o alvo é a peça toda.
 *
 * <h2>A nota é parte da decisão, não decoração</h2>
 *
 * <p>{@link OpcaoDoSeletor.nota} é a linha miúda dentro da opção — na tela de
 * etiquetas ela diz que <b>aquele</b> tambor já teve etiqueta, o que faz a
 * escolha exigir motivo. Ela herda a cor do botão de propósito: no estado
 * escolhido o assento é verde sólido, e uma tinta cinza fixa sumiria
 * justamente na opção que a pessoa acabou de marcar.
 */
export interface OpcaoDoSeletor {
  /** Chave estável — e o que volta em {@link Props.aoAlternar}. */
  id: string;
  /** O que se lê grande. Curto: um número, um código. */
  rotulo: string;
  /** A linha miúda embaixo — o que **este** item tem de diferente. */
  nota?: string | undefined;
  /**
   * O nome inteiro para quem ouve a tela.
   *
   * <p>"17" sozinho não diz nada num leitor de tela; "Volume 17, 1 via já
   * emitida" diz. Sem ele, o nome acessível é o texto visível.
   */
  descricao?: string | undefined;
}

interface Props {
  /** Nomeia o grupo — é o que o leitor de tela anuncia antes das opções. */
  rotulo: string;
  opcoes: readonly OpcaoDoSeletor[];
  /** Os `id` escolhidos. */
  selecionados: readonly string[];
  aoAlternar(id: string): void;
}

export function SeletorMultiplo({ rotulo, opcoes, selecionados, aoAlternar }: Props) {
  return (
    <div className="ui-selecao" role="group" aria-label={rotulo}>
      {opcoes.map((opcao) => {
        const escolhida = selecionados.includes(opcao.id);
        return (
          <button
            key={opcao.id}
            type="button"
            className={`ui-selecao__opcao${escolhida ? " ui-selecao__opcao--escolhida" : ""}`}
            aria-pressed={escolhida}
            aria-label={opcao.descricao}
            onClick={() => aoAlternar(opcao.id)}
          >
            <span className="ui-selecao__rotulo">{opcao.rotulo}</span>
            {opcao.nota !== undefined && (
              <span className="ui-selecao__nota">{opcao.nota}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
